import { FocusMonitor } from '@angular/cdk/a11y'
import { coerceBooleanProperty } from '@angular/cdk/coercion'
import { Component, ElementRef, HostBinding, Inject, Input, OnDestroy, OnInit, Optional, Self, ViewChild } from '@angular/core'
import { AbstractControl, ControlValueAccessor, NgControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms'
import { MAT_FORM_FIELD, MatFormField, MatFormFieldControl } from '@angular/material/form-field'
import { Subject } from 'rxjs'
import { Utils } from '../../../../../server/src/shared-with-client/utils'

@Component({
    selector: 'app-input-phone',
    templateUrl: './input-phone.component.html',
    styleUrls: ['./input-phone.component.scss'],
    providers: [{ provide: MatFormFieldControl, useExisting: InputPhoneComponent }],
    standalone: false,
})
export class InputPhoneComponent implements ControlValueAccessor, MatFormFieldControl<string>, OnDestroy, OnInit {
    static nextId = 0
    static ngAcceptInputType_disabled: boolean | string | null | undefined
    static ngAcceptInputType_required: boolean | string | null | undefined

    @HostBinding('id') id = `phone-number-input-${InputPhoneComponent.nextId++}`
    @HostBinding('class.label-floating') get float() {
        return this.shouldLabelFloat
    }
    @ViewChild('area') areaInput!: HTMLInputElement
    @ViewChild('office') officeInput!: HTMLInputElement
    @ViewChild('subscriber') subscriberInput!: HTMLInputElement

    parts: UntypedFormGroup
    stateChanges = new Subject<void>()
    focused = false

    get empty() {
        const {
            value: { area, office, subscriber },
        } = this.parts

        return !area && !office && !subscriber
    }

    get shouldLabelFloat() {
        return this.focused || !this.empty
    }

    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('aria-describedby') userAriaDescribedBy = ''

    @Input()
    get placeholder(): string {
        return this._placeholder
    }
    set placeholder(value: string) {
        this._placeholder = value
        this.stateChanges.next()
    }
    private _placeholder = ''

    @Input()
    get required(): boolean {
        return this._required
    }
    set required(value: boolean) {
        this._required = coerceBooleanProperty(value)
        this.stateChanges.next()
    }
    private _required = false

    @Input()
    get disabled(): boolean {
        return this._disabled
    }
    set disabled(value: boolean) {
        this._disabled = coerceBooleanProperty(value)
        this._disabled ? this.parts.disable() : this.parts.enable()
        this.stateChanges.next()
    }
    private _disabled = false

    @Input()
    get value(): string | null {
        const {
            value: { area, office, subscriber },
        } = this.parts

        return `${area}${office}${subscriber}`
    }
    set value(tel: string | null) {
        const { area, office, subscriber } = tel ? Utils.User.formatStringToPhone(tel) : { area: '', office: '', subscriber: '' }

        this.parts.setValue({ area, office, subscriber })
        this.stateChanges.next()
    }

    get errorState(): boolean {
        return (this.ngControl.touched && this.ngControl.invalid) || (this.parts.invalid && this.parts.dirty)
    }

    onChange = (_: string | null) => {}
    onTouched = () => {}

    constructor(
        formBuilder: UntypedFormBuilder,
        private _focusMonitor: FocusMonitor,
        private _elementRef: ElementRef<HTMLElement>,
        @Optional() @Inject(MAT_FORM_FIELD) public _formField: MatFormField,
        @Optional() @Self() public ngControl: NgControl
    ) {
        this.parts = formBuilder.group({
            area: [null, [Validators.required, Validators.minLength(3), Validators.maxLength(3)]],
            office: [null, [Validators.required, Validators.minLength(3), Validators.maxLength(3)]],
            subscriber: [null, [Validators.required, Validators.minLength(4), Validators.maxLength(4)]],
        })

        _focusMonitor.monitor(_elementRef, true).subscribe((origin) => {
            if (this.focused && !origin) {
                this.onTouched()
            }
            this.focused = !!origin
            this.stateChanges.next()
        })

        if (this.ngControl != null) {
            this.ngControl.valueAccessor = this
        }
    }

    autoFocusNext(control: AbstractControl, nextElement?: HTMLInputElement): void {
        if (!control.errors && nextElement) {
            this._focusMonitor.focusVia(nextElement, 'program')
        }
    }

    autoFocusPrev(control: AbstractControl, prevElement: HTMLInputElement): void {
        if (control.value.length < 1) {
            this._focusMonitor.focusVia(prevElement, 'program')
        }
    }

    ngOnInit() {
        this.ngControl.statusChanges?.subscribe(() => {
            // trigger changes to show mat error in parent mat form field
            this.stateChanges.next()
        })
    }

    ngOnDestroy() {
        this.stateChanges.complete()
        this._focusMonitor.stopMonitoring(this._elementRef)
    }

    setDescribedByIds(ids: string[]) {
        const controlElement = this._elementRef.nativeElement.querySelector('.phone-number-input-container')

        if (!controlElement) {
            return
        }

        controlElement.setAttribute('aria-describedby', ids.join(' '))
    }

    onContainerClick() {
        if (this.parts.controls.subscriber.valid) {
            this._focusMonitor.focusVia(this.subscriberInput, 'program')
        } else if (this.parts.controls.office.valid) {
            this._focusMonitor.focusVia(this.subscriberInput, 'program')
        } else if (this.parts.controls.area.valid) {
            this._focusMonitor.focusVia(this.officeInput, 'program')
        } else {
            this._focusMonitor.focusVia(this.areaInput, 'program')
        }
    }

    writeValue(tel: string | null): void {
        this.value = tel
    }

    registerOnChange(fn: (_: string | null) => void): void {
        this.onChange = fn
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled
    }

    removeNonNumberValue(control: AbstractControl) {
        const digits = Utils.User.getDigits(control.value)

        control.setValue(digits)
    }

    _handleInput(control: AbstractControl, nextElement?: HTMLInputElement): void {
        this.removeNonNumberValue(control)
        this.autoFocusNext(control, nextElement)
        this.onChange(this.value)
    }
}
